import Phaser from 'phaser';
import { gameConfig } from '@/config/gameConfig';
import { SCENE_KEYS } from '@/config/constants';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { AudioManager } from '@/utils/AudioManager';
import { CinematicShader } from '@/pipelines/CinematicPipeline';
import { CausticsTitleShader } from '@/pipelines/CausticsTitlePipeline';
import { BootScene } from '@/scenes/BootScene';
import { MenuScene } from '@/scenes/MenuScene';
import { NameEntryScene } from '@/scenes/NameEntryScene';
import { GameScene } from '@/scenes/GameScene';
import { UIScene } from '@/scenes/UIScene';
import { GameOverScene } from '@/scenes/GameOverScene';
import { StageClearScene } from '@/scenes/StageClearScene';
import { LeaderboardScene } from '@/scenes/LeaderboardScene';
import { BossScene } from '@/scenes/BossScene';
import { TutorialScene } from '@/scenes/TutorialScene';
import { CreditsScene } from '@/scenes/CreditsScene';
import { SettingsScene } from '@/scenes/SettingsScene';
import { ControlsScene } from '@/scenes/ControlsScene';

/**
 * Poseidon's Challenge - Main Entry Point
 *
 * This file also installs the kiosk-survival reliability handlers
 * (global errors, WebGL context loss, AudioContext resume on focus)
 * needed for the multi-day exhibition deployment.
 */

console.log('🌊 Initializing Poseidon\'s Challenge...');

// Phaser 4 replaces the v3 `pipeline:` global registration with `render.renderNodes`.
// Each custom shader-based filter is registered here once and then referenced
// by name from its Filters.Controller subclass.
const config: Phaser.Types.Core.GameConfig = {
  ...gameConfig,
  render: {
    ...gameConfig.render,
    // Phaser's TS types declare this as `{ key?, function? }`, but the runtime
    // unpacks each value AS-IS into `addNodeConstructor(name, constructor)` so
    // we must pass the class directly. Cast through `unknown` to bypass.
    renderNodes: {
      CinematicShader,
      CausticsTitleShader,
    } as unknown as { [k: string]: Phaser.Types.Core.RenderNodesConfig },
  },
  scene: [
    BootScene,
    MenuScene,
    NameEntryScene,
    GameScene,
    UIScene,
    GameOverScene,
    StageClearScene,
    LeaderboardScene,
    BossScene,
    TutorialScene,
    CreditsScene,
    SettingsScene,
    ControlsScene,
  ],
};

// ─── Reliability handlers for the 12h exhibition kiosk ──────────────────────

/**
 * Reload the page with debouncing so a flapping error path can't infinite-loop.
 * The kiosk is unattended, so a hard reload is the only universally safe
 * recovery from boot-time crashes, lost WebGL contexts, etc.
 */
const RELOAD_COOLDOWN_MS = 30_000;
let lastReloadAt = 0;
function safeReload(reason: string, delayMs: number = 3000): void {
  const now = Date.now();
  if (now - lastReloadAt < RELOAD_COOLDOWN_MS) {
    console.warn(`⏸ Skipping reload (${reason}), cooldown active`);
    return;
  }
  lastReloadAt = now;
  console.warn(`🔁 Reloading in ${delayMs}ms: ${reason}`);
  window.setTimeout(() => {
    try { window.location.reload(); } catch {}
  }, delayMs);
}

/**
 * Catch any uncaught error at the window level. Only reload on a true
 * runaway loop (50+ uncaught errors in 10 seconds), not on the occasional
 * stray. The previous threshold (5 in 30s) was kicking players out of the
 * boss scene whenever a tween or timer fired against a destroyed object,
 * which triggered a few benign uncaught errors that should not interrupt
 * play. Reserve the auto reload for actual unrecoverable error storms.
 */
let errorBurst = 0;
let errorBurstAt = 0;
window.addEventListener('error', (ev) => {
  console.error('🛑 Uncaught error:', ev.error || ev.message);
  const now = Date.now();
  if (now - errorBurstAt > 10_000) {
    errorBurst = 0;
    errorBurstAt = now;
  }
  errorBurst++;
  if (errorBurst >= 50) {
    safeReload('error storm (50+ uncaught errors in 10s)');
  }
});

window.addEventListener('unhandledrejection', (ev) => {
  console.error('🛑 Unhandled promise rejection:', ev.reason);
});

// ─── Create the game ─────────────────────────────────────────────────────────

let game: Phaser.Game;
try {
  game = new Phaser.Game(config);
} catch (err) {
  console.error('🛑 Phaser.Game construction failed:', err);
  safeReload('boot crash', 5000);
  // Re-throw so the dev console still shows the stack while the kiosk reloads.
  throw err;
}

// Log game information
console.log('🎮 Game initialized');
console.log(`📐 Resolution: ${config.width}x${config.height}`);
console.log(`⚙️  Renderer: ${config.type === Phaser.AUTO ? 'AUTO' : config.type === Phaser.WEBGL ? 'WebGL' : 'Canvas'}`);

// ─── WebGL context loss recovery ─────────────────────────────────────────────
// Long-running WebGL on shared hardware (museum displays, kiosks) loses its
// context every few hours under memory pressure. Phaser cannot fully recover
// from this once custom pipelines are wired up, so we reload as a last resort.
try {
  const canvas: HTMLCanvasElement | undefined = (game as any).canvas;
  if (canvas) {
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      safeReload('webglcontextlost');
    }, { passive: false } as AddEventListenerOptions);
    canvas.addEventListener('webglcontextrestored', () => {
      console.warn('🟢 WebGL context restored (Phaser may not redraw pipelines, reload as safety)');
      safeReload('webglcontextrestored');
    });
  }
} catch (err) {
  console.warn('Could not attach WebGL context handlers:', err);
}

// ─── Frozen game-loop watchdog ───────────────────────────────────────────────
// A fatal error thrown inside Phaser's step() kills the requestAnimationFrame
// loop, leaving a frozen black screen. The error-burst handler above won't
// catch this (it's a single throw, not a storm), so the kiosk would sit dead.
// The Phaser loop bumps a heartbeat every frame; an independent interval (which
// keeps ticking even after RAF dies) reloads if the loop goes quiet while the
// tab is visible.
let lastHeartbeat = Date.now();
let heartbeatSeen = false; // only arm the freeze check once the loop proves alive
try {
  game.events.on(Phaser.Core.Events.POST_STEP, () => {
    lastHeartbeat = Date.now();
    heartbeatSeen = true;
  });
} catch (e) {
  console.warn('Could not attach loop heartbeat:', e);
}
// A backgrounded tab throttles both RAF and timers, which would read as a
// freeze, so reset the clock whenever we return to the foreground.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) lastHeartbeat = Date.now();
});
const LOOP_FREEZE_MS = 8000;
window.setInterval(() => {
  if (document.hidden) return;
  // If the heartbeat never fired, the hook isn't wired — stay passive rather
  // than risk a reload loop on a build where the event name differs.
  if (!heartbeatSeen) return;
  if (Date.now() - lastHeartbeat > LOOP_FREEZE_MS) {
    safeReload(`game loop frozen (no frame for ${LOOP_FREEZE_MS}ms)`);
  }
}, 2000);

// ─── Idle / stuck-dialog watchdog (total reset) ──────────────────────────────
// Unattended kiosk: if a blocking dialog or an idle static screen (game over,
// stage clear, settings, controls, leaderboard, boss victory) gets no user
// input for 40s, force a full page reload back to the attract loop. Active
// gameplay, the main menu (its own attract demo handles idle) and the credits
// crawl are intentionally exempt.
const IDLE_RESET_MS = 40_000;
const watchdog = KioskWatchdog.instance;
const noteActivity = () => watchdog.noteActivity();
window.addEventListener('pointerdown', noteActivity, { capture: true, passive: true });
window.addEventListener('pointermove', noteActivity, { capture: true, passive: true });
window.addEventListener('keydown', noteActivity, { capture: true });
// Gamepad input never reaches the DOM, so poll it: a held stick or any pressed
// button means the player is present, so the screen must not auto-reset.
window.setInterval(() => {
  try {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) {
      if (!p) continue;
      if (p.buttons.some((b) => b && b.pressed)) { watchdog.noteActivity(); break; }
      if (p.axes.some((a) => Math.abs(a) > 0.4)) { watchdog.noteActivity(); break; }
    }
  } catch {}
}, 300);

const STATIC_RESET_SCENES: string[] = [
  SCENE_KEYS.NAME_ENTRY,
  SCENE_KEYS.GAME_OVER,
  SCENE_KEYS.STAGE_CLEAR,
  SCENE_KEYS.SETTINGS,
  SCENE_KEYS.CONTROLS,
  SCENE_KEYS.LEADERBOARD,
];
// Soft reset to attract — return to the menu WITHOUT a full page reload, so the
// warm asset cache survives and the next player gets an INSTANT attract screen
// instead of waiting through the whole boot load again. MenuScene is already a
// clean slate (its init/create clears the player identity and stops any
// lingering GAME/UI/BOSS scenes), so all we add here is stopping the end-screens
// and resetting shared kiosk state. Returns false if anything throws, so the
// caller can fall back to a hard reload.
function softResetToAttract(reason: string): boolean {
  try {
    console.log(`🔄 Soft reset to attract: ${reason}`);
    KioskWatchdog.instance.reset();
    try { AudioManager.getInstance().stopMusic(); } catch {}
    try { game.registry.remove('playerName'); game.registry.remove('playerId'); } catch {}
    // Stop every scene except the loader and the menu, then (re)start the menu.
    for (const s of game.scene.scenes) {
      const key = s.scene.key;
      if (key !== SCENE_KEYS.BOOT && key !== SCENE_KEYS.MENU) {
        try { game.scene.stop(key); } catch {}
      }
    }
    game.scene.start(SCENE_KEYS.MENU);
    KioskWatchdog.instance.noteActivity();
    return true;
  } catch (e) {
    console.error('Soft reset to attract failed:', e);
    return false;
  }
}

// Periodically force a REAL reload (instead of a soft reset) so any slow memory
// creep over a multi-hour run is cleared — but only while the kiosk is idle, so
// it is never seen mid-play. Every other idle reset is soft and instant.
let lastFullReloadAt = Date.now();
let softResetsSinceFull = 0;
const PERIODIC_FULL_RELOAD_MS = 30 * 60_000;
// Bound memory by TURNOVER COUNT as well as by time: after this many soft
// resets, the next return-to-attract does a real page reload instead. So the
// asset/GPU memory is fully cleared at least every ~N players regardless of
// idle timing (covers a continuously-busy kiosk with no 30-min idle gap), while
// ~N-1 of every N turnovers stay instant. The reload always lands during idle,
// so no player ever waits through it.
const SOFT_RESETS_PER_FULL = 8;
// The boss arena no longer has a countdown (removed so a present-but-struggling
// player is never game-over'd by a clock). That left a gap: a player who walks
// away MID boss-fight would otherwise strand the kiosk on the arena, because
// active gameplay is exempt from the idle reset and the boss now has no timeout.
// Recover an ABANDONED boss with a generous input-silence window — an actively
// playing player inputs far more often than this, so a real fight is never cut
// off; only a truly abandoned one is.
const BOSS_IDLE_MS = 60_000;
window.setInterval(() => {
  if (document.hidden) return;
  const idleFor = Date.now() - watchdog.lastActivity;
  let watched = false;
  // Abandoned end-screen / open dialog / boss-victory card → short 40s window.
  if (idleFor > IDLE_RESET_MS) {
    watched = watchdog.modalOpen || watchdog.bossVictory;
    if (!watched) {
      try { watched = STATIC_RESET_SCENES.some((k) => game.scene.isActive(k)); } catch {}
    }
  }
  // Abandoned boss fight → longer 60s window (it is active gameplay). Skip the
  // attract-mode autoplay boss: it has no human input by design and manages its
  // own looping, so the idle window must not yank it back to the menu.
  if (!watched && idleFor > BOSS_IDLE_MS) {
    try {
      const bossScene = game.scene.getScene(SCENE_KEYS.BOSS) as { isAutoPlay?: boolean } | null;
      watched = game.scene.isActive(SCENE_KEYS.BOSS) && !bossScene?.isAutoPlay;
    } catch {}
  }
  if (!watched) return;
  // Hand the kiosk back to attract for the next player. Prefer the instant soft
  // reset; periodically (or if the soft path throws) do a full reload to clear
  // memory while no one is watching.
  const dueForFull = softResetsSinceFull >= SOFT_RESETS_PER_FULL
    || Date.now() - lastFullReloadAt > PERIODIC_FULL_RELOAD_MS;
  if (dueForFull) {
    lastFullReloadAt = Date.now();
    softResetsSinceFull = 0;
    safeReload(`idle — full reset to attract (every ${SOFT_RESETS_PER_FULL} turnovers / 30min, clears memory)`, 1500);
  } else if (softResetToAttract('idle — return to attract')) {
    softResetsSinceFull += 1;
  } else {
    // Soft reset threw — fall back to the old full-reload behaviour.
    lastFullReloadAt = Date.now();
    softResetsSinceFull = 0;
    safeReload('idle — soft reset failed, full reload', 1500);
  }
}, 2000);

// ─── AudioContext resume on focus / visibility ───────────────────────────────
// When the kiosk tab is backgrounded (OS popup, screensaver), the AudioContext
// is suspended and Phaser does not resume it on its own. Without this, music
// goes silent permanently until the page is reloaded.
function resumeAudio(): void {
  try {
    const ctx: AudioContext | undefined = (game.sound as any)?.context;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch((e) => console.warn('AudioContext.resume rejected:', e));
    }
  } catch {}
}
document.addEventListener('visibilitychange', () => { if (!document.hidden) resumeAudio(); });
window.addEventListener('focus', resumeAudio);

// ─── Gamepad disconnect recovery ─────────────────────────────────────────────
// At the exhibition, players may yank the controller mid game. Without a
// signal the cursor or selection halo just freezes and the player thinks the
// game is broken. We show a brief toast at the top of the screen telling
// them to hand the joystick back, and dismiss it the moment any pad reconnects
// or any mouse/keyboard input is detected.
(function installGamepadDisconnectToast() {
  let toast: HTMLDivElement | undefined;
  function ensureToast(): HTMLDivElement {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.id = 'gamepad-disconnect-toast';
    toast.textContent = 'CONTROLLER DISCONNECTED. Please reconnect or use mouse / keyboard.';
    toast.style.cssText = [
      'position:fixed', 'top:24px', 'left:50%', 'transform:translateX(-50%)',
      'background:#0a1426', 'color:#FFD27A',
      'border:2px solid #C28A3E',
      'border-radius:14px',
      'padding:14px 26px',
      'font-family:Archivo, system-ui, sans-serif',
      'font-weight:700', 'font-size:18px', 'letter-spacing:1px',
      'box-shadow:0 8px 26px rgba(0,0,0,0.6)',
      'z-index:99999',
      'pointer-events:none',
      'display:none',
    ].join(';');
    document.body.appendChild(toast);
    return toast;
  }
  function show() {
    const t = ensureToast();
    t.style.display = 'block';
  }
  function hide() {
    if (toast) toast.style.display = 'none';
  }
  window.addEventListener('gamepaddisconnected', () => {
    // Only show if no other pad is still connected.
    try {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      const stillConnected = Array.from(pads).some((p) => p && p.connected);
      if (!stillConnected) show();
    } catch {
      show();
    }
  });
  window.addEventListener('gamepadconnected', hide);
  // Hand the kiosk back to mouse / keyboard if the player gives up on the pad.
  window.addEventListener('pointerdown', hide, { capture: true });
  window.addEventListener('keydown', hide, { capture: true });
})();

// Make game accessible in console for debugging
if (typeof window !== 'undefined') {
  (window as any).game = game;
  console.log('💡 Game instance available at window.game');
}

// Export game instance
export default game;
